import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import {
    AddActivityDataFormValues,
    AddActivityDataFormValidationSchema,
    defaultAddActivityDataFormValues,
} from "./AddActivityDataFormValues";
import FormTextInput from "../FormTextInput";
import FormDateInput from "../FormDateInput";
import Button from "../Button";
import { AddActivityDataFormValuesBase } from "./AddActivityDataFormValues";
import { AddActivityDataDto } from "../../api/models/ActivityData";
import { addDays, dateAtMidnight, intervalInDays } from "../../utils/dateUtils";
import { useState } from "react";
import { ArrowsHorizontal } from "@phosphor-icons/react";

type AddActivityDataFormProps = {
    onSubmit: (values: AddActivityDataDto) => void;
    onCancel: () => void;
    isSubmitting: boolean;
    rawUnitsSuffix: string;
};

const AddActivityDataForm = ({
    onSubmit,
    onCancel,
    isSubmitting,
    rawUnitsSuffix,
}: AddActivityDataFormProps) => {
    const methods = useForm<AddActivityDataFormValuesBase>({
        mode: "onSubmit",
        resolver: yupResolver(
            AddActivityDataFormValidationSchema(rawUnitsSuffix)
        ),
        defaultValues: defaultAddActivityDataFormValues,
    });

    const handleSubmit = async (values: AddActivityDataFormValues) => {
        const midnightDate = dateAtMidnight(values.activityDate);

        const endDate = values.activityEndDate;

        const dto: AddActivityDataDto = {
            rawActivityQuantity: values.rawActivityQuantity,
            activityStartDate: midnightDate.toISOString(),
            activityEndDate: endDate.toISOString()
        };

        onSubmit(dto);
    };

    const activityQuantityLabel = `Activity quantity (${rawUnitsSuffix})`;

    const [periodLength, setPeriodLength] = useState(1);
    const [prevStart, setPrevStart] = useState<Date | null>(null);
    const [prevEnd, setPrevEnd] = useState<Date | null>(null);

    const currentStartDate = methods.watch("activityDate");
    const currentEndDate = methods.watch("activityEndDate");

    if (prevEnd?.getTime() !== currentEndDate.getTime()) {
        setPrevEnd(currentEndDate);
        setPeriodLength(intervalInDays(currentStartDate, currentEndDate));
    }

    if (prevStart?.getTime() !== currentStartDate.getTime()) {
        const newEndDate = addDays(currentStartDate, periodLength);
        methods.setValue("activityEndDate", newEndDate);
        setPrevStart(currentStartDate);
        setPrevEnd(newEndDate);
    }
    
    return (
        <form
            onSubmit={methods.handleSubmit(handleSubmit)}
            className="space-y-4"
        >
            <FormTextInput
                required
                label={activityQuantityLabel}
                placeholder="0"
                type="number"
                min={0}
                max={1000000}
                step={0.0000001}
                {...methods.register("rawActivityQuantity")}
                error={methods.formState.errors.rawActivityQuantity}
            />
            <div className="flex flex-row items-center">
            <Controller
                name="activityDate"
                control={methods.control}
                render={({ field: { onChange, value } }) => (
                    <FormDateInput
                        required
                        id="add-activity-date"
                        label="Activity start date"
                        helperText="DD/MM/YYYY"
                        onChange={onChange}
                        value={value}
                        maxDate={new Date()}
                        error={methods.formState.errors.activityDate}
                    />
                )}
            />
            <ArrowsHorizontal className="m-3 flex-none" size="20"/>
            <Controller
                name="activityEndDate"
                control={methods.control}
                render={({ field: { onChange, value } }) => (
                    <FormDateInput
                        required
                        id="add-activity-end-date"
                        label="Activity end date"
                        helperText="DD/MM/YYYY"
                        onChange={onChange}
                        value={value}
                        maxDate={new Date()}
                        error={methods.formState.errors.activityEndDate}
                    />
                )}
            />
            </div>
            
            <div className="flex justify-end gap-6 pt-4">
                <Button
                    variant="transparent"
                    type="button"
                    disabled={isSubmitting}
                    onClick={onCancel}
                >
                    Cancel
                </Button>
                <Button type="submit" loading={isSubmitting}>
                    Submit
                </Button>
            </div>
        </form>
    );
};

export default AddActivityDataForm;
